import React, { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

interface DashboardCard {
  icon: string;
  title: string;
  value: string;
  subtitle: string;
}

const mockFetchDashboardData = () =>
  Promise.resolve({
    todayTrips: 17,
    todayRevenue: 14500,
    tripsThisWeek: [
      { date: "Mon", trips: 12, revenue: 9000 },
      { date: "Tue", trips: 17, revenue: 14500 },
      { date: "Wed", trips: 14, revenue: 11000 },
      { date: "Thu", trips: 20, revenue: 16800 },
      { date: "Fri", trips: 10, revenue: 8000 },
      { date: "Sat", trips: 13, revenue: 9500 },
      { date: "Sun", trips: 18, revenue: 15000 },
    ],
  });

const SuperAdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<any>(null);

  useEffect(() => {
    setLoading(true);
    mockFetchDashboardData().then((data) => {
      setDashboard(data);
      setLoading(false);
    });
  }, []);

  if (loading)
    return (
      <div className="p-10 text-center text-lg text-slate-500 dark:text-slate-400">
        Loading...
      </div>
    );

  const cards: DashboardCard[] = [
    {
      icon: "🚌",
      title: "Today's Trips",
      value: `${dashboard.todayTrips}`,
      subtitle: "Trips completed today",
    },
    {
      icon: "💰",
      title: "Today's Revenue",
      value: `₹${(dashboard.todayRevenue ?? 0).toLocaleString()}`,
      subtitle: "Revenue generated today",
    },
    {
      icon: "📊",
      title: "Weekly Trips",
      value: `${dashboard.tripsThisWeek.reduce((sum: number, d: { trips: number }) => sum + d.trips, 0)}`,
      subtitle: "Total trips this week",
    },
  ];

  return (
    <div>
      <p className="text-slate-700 dark:text-slate-300 text-base mb-6">
        Welcome to Sitaram Transport! Here’s your dashboard overview:
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {cards.map((card, idx) => (
          <div
            key={idx}
            className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-md hover:shadow-lg transition-shadow"
          >
            <h2 className="text-lg font-semibold text-primary mb-1 flex items-center gap-2">
              <span>{card.icon}</span> {card.title}
            </h2>
            <p className="text-2xl font-semibold text-dark dark:text-white">{card.value}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{card.subtitle}</p>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-md mt-6">
        <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          📈 Trips & Revenue (Last 7 Days)
        </h2>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dashboard.tripsThisWeek}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
              <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="trips" fill="#8884d8" name="Trips" />
              <Bar yAxisId="right" dataKey="revenue" fill="#82ca9d" name="Revenue" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
